// Modal.tsx

import React, { useEffect, useState } from 'react';
import { Button, DropDown } from '../../Atoms';
import CustomizedDatePicker from '../../Atoms/DatePicker';
import InputLabel from '../../Atoms/InputLabel';
import SingleChoice from '../../Atoms/SingleChoice';
import { ListTypes, POATypes, QuestionnaireResponseStatus } from '@/constants/enums';
import dayjs from 'dayjs';
import { IPoaModalItem } from '@/components/survey/SurveyModel/types/nodeTypes';
import { useMoveToPoa } from '@/state/survey/poaSurveySlice';
import Group from '@/assets/Group';
import Caller from '@/assets/Caller';
import None from '@/assets/None';

const YES_NO_OPTIONS = ['Yes', 'No'];

const POA_OPTIONS = [
    { value: POATypes.FaceToFace, label: 'Face to face', icon: (isHover: boolean) => <div className='w-[26px] flex justify-center'><Group color={isHover ? "white" : undefined} /></div> },
    { value: POATypes.PhoneCall, label: 'Phone call', icon: (isHover: boolean) => <div className='w-[26px] flex justify-center'><Caller color={isHover ? "white" : undefined} /></div> },
    { value: 3, label: 'None', icon: (isHover: boolean) => <div className='w-[26px] flex justify-center'><None color={isHover ? "white" : undefined} /></div> },
];

interface TriageListModalProps {
    isOpen: boolean;
    onClose: (poaModalItem: IPoaModalItem | null) => void;
    name: string;
    questionnaireResponseId: number
    waitingListRecordId: number,
    existingPOADateTime?: Date,
    submitButtonText: string
    type: ListTypes
}

const TriageListModal: React.FC<TriageListModalProps> = ({ isOpen, onClose, name, waitingListRecordId, existingPOADateTime, submitButtonText }) => {
    const [poaDate, setPoaDate] = useState<string | null>(null);
    const [selectedPOA, setSelectedPOA] = useState<{ value: number; label: string } | null>(null);
    const [bookedInTrakCare, setBookedInTrakCare] = useState<boolean | null>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    

    useEffect(() => {
        if (existingPOADateTime != null && (!isNaN(existingPOADateTime.getTime()))) {
            setPoaDate(dayjs(existingPOADateTime).toISOString());
        } else {
            setPoaDate('');
        }

        return () => {
           setPoaDate(null)
           setSelectedPOA(null)
        }
    }, []);

    useEffect(() => {
        setPoaDate(null);
        setSelectedPOA(null);
        setBookedInTrakCare(false);
    }, [isOpen]);

    const moveToPoa = useMoveToPoa();

    const handleDropdownChange = (selectedOption: { value: number; label: string } | null) => {
        setSelectedPOA(selectedOption);
    };

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            const dateTime = poaDate ? poaDate + "Z" : null;
            await moveToPoa(waitingListRecordId, QuestionnaireResponseStatus.POA, selectedPOA?.value as POATypes, dateTime, !!bookedInTrakCare);
            onClose({
                id: waitingListRecordId,
                poaDateTime: dateTime || "",
                poaType: selectedPOA?.value as POATypes
            });
        } catch (error) {
            console.error("Error updating POA:", error);
            onClose(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancelOnClick = () => {
        onClose(null);
        setPoaDate(null);
        setSelectedPOA(null);
        setBookedInTrakCare(false);
    }

    const handleBookedInTrakCareChange = (val: string) => {
        setBookedInTrakCare(val === YES_NO_OPTIONS[0]);
        if (val === YES_NO_OPTIONS[1]) {
            setSelectedPOA(null);
            setPoaDate(null);
        }
    };



    if (!isOpen) return null;

    const isMoveToPoaSubmitEnabled =
        bookedInTrakCare === false ||
        (bookedInTrakCare === true && selectedPOA != null && !isSubmitting)

    return (
        <div className="triage-modal modal-overlay" onClick={() => onClose(null)}>
            <div className="modal-content modal-content-triage" onClick={(e) => e.stopPropagation()}>
                <div className='modal-form'>
                    <div className='form-content'>
                        <h1 className='modal-header'>{name}</h1>

                        <div className={`user-input user-input-triage radio-padding field`}>
                            <InputLabel label={'Has the POA appointment been booked in TrakCare?'} labelClasses="input-label"></InputLabel>
                            <SingleChoice
                                options={YES_NO_OPTIONS}
                                name="bookedInTrakCare"
                                value={bookedInTrakCare === true ? 'Yes' : bookedInTrakCare === false ? 'No' : ''}
                                onChange={handleBookedInTrakCareChange}
                                onBlur={() => {}}
                                propClasses="flex move-to-poa-options"
                            />
                        </div>

                        {bookedInTrakCare && (
                            <>
                                <div className={`user-input drop-down user-input-triage field`}>
                                    <InputLabel label={'Type of POA'} labelClasses="input-label"></InputLabel>
                                    <div className="drop-down-field">
                                        <DropDown
                                            propDivClasses={'custom-select-triage w-[162px]'}
                                            handleChange={handleDropdownChange}
                                            options={POA_OPTIONS}
                                            value={selectedPOA}
                                            isInValid={false}
                                        />
                                    </div>
                                </div>

                                <div className='row-container'>
                                    <div className="user-input-wrapper input-date user-input-triage field">
                                        <InputLabel label={'Enter the appointment date and time'} labelClasses="input-label" />
                                        <CustomizedDatePicker
                                            onChange={(date: string | null) => setPoaDate(date)}
                                            className="w-[320px]"
                                            initialDate={poaDate}
                                            showTime
                                        />
                                    </div>

                                    {/* <div className="user-input-wrapper input-time user-input-triage field">
                                        <InputLabel label={'Time'} labelClasses="input-label" />
                                        <TimePicker
                                            onChange={(time: any) => setPoaTime(time)}
                                            initialTime={poaTime}
                                        />
                                    </div> */}
                                </div>

                            </>
                        )}
                    </div>
                    <div className='button-container button-container-triage-list button-spacing'>
                        <Button 
                            type='tertiary'
                            text={submitButtonText} 
                            icon={false} handleClick={handleSubmit} 
                            stateProp={isMoveToPoaSubmitEnabled ? 'enabled' : 'disabled'}
                        />
                        <Button 
                            type='primary' 
                            text="Cancel" 
                            icon={false} 
                            handleClick={handleCancelOnClick} 
                            stateProp='enabled' 
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};


export default TriageListModal;
